import { capBody, countComments, cutText, safeHref, threadComments, type Comment, type ParsedDiscussion, type RawComment, type Run } from "./discussion";

export const DISCUSSION_VERSION = 2;
export const WINDOW_BYTES = 100_000;
export const WINDOW_RECORDS = 500;
export interface DiscussionRecord extends RawComment { to?: string; replyCount?: number; replyNext?: string }
export interface DiscussionWindow {
  version: 2; state: "ok" | "empty" | "closed" | "none";
  records: DiscussionRecord[]; link: string; fetchedAt: number; total?: number;
  next?: string; partial?: boolean; phoneCopy?: boolean;
}
export type DiscussionFailure = "failed" | "blocked" | "changed" | "disabled" | "waiting" | "restricted";
export type DiscussionResult = { kind: "window"; window: DiscussionWindow } | { kind: "unavailable"; reason: DiscussionFailure; link: string; checkedAt: number; cached?: DiscussionWindow };
export interface DiscussionCursor { key: string; scope: "roots" | "replies" | "stream"; after?: string; prefix?: string; root?: string; source?: string }
export const unavailable = (reason: DiscussionFailure, link: string, checkedAt = Date.now()): DiscussionResult => ({ kind: "unavailable", reason, link, checkedAt });
export const jsonBytes = (value: unknown): number => { try { return new TextEncoder().encode(JSON.stringify(value)).length; } catch { return Infinity; } };

/** A boundary checksum, not an authentication token. It binds continuation to its article and ordered IDs. */
export function discussionHash(text: string): string {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) hash = Math.imul(hash ^ text.charCodeAt(i), 16777619);
  return (hash >>> 0).toString(36);
}
export const discussionKey = (link: string): string => {
  try {
    const u = new URL(link);
    for (const key of [...u.searchParams.keys()]) if (key === "trk" || key.startsWith("utm_")) u.searchParams.delete(key);
    u.searchParams.sort();
    return discussionHash(`${u.hostname.replace(/^www\./, "")}${u.pathname.replace(/\/$/, "")}?${u.searchParams}`);
  } catch { return discussionHash(link.replace(/#.*$/, "")); }
};
export const encodeDiscussionCursor = (cursor: DiscussionCursor): string => `d2:${encodeURIComponent(JSON.stringify(cursor))}`;
export function decodeDiscussionCursor(text: string | undefined, link: string): DiscussionCursor | null {
  if (!text) return { key: discussionKey(link), scope: "roots" };
  if (text.length > 4096 || !text.startsWith("d2:")) return null;
  try {
    const c: unknown = JSON.parse(decodeURIComponent(text.slice(3)));
    if (!object(c) || c.key !== discussionKey(link) || !["roots", "replies", "stream"].includes(String(c.scope))) return null;
    for (const field of ["after", "prefix", "root", "source"]) if (c[field] !== undefined && (typeof c[field] !== "string" || c[field].length > 2048)) return null;
    if (c.scope === "replies" && typeof c.root !== "string") return null;
    return c as unknown as DiscussionCursor;
  } catch { return null; }
}
const object = (x: unknown): x is Record<string, unknown> => !!x && typeof x === "object" && !Array.isArray(x);
const validText = (x: unknown): x is string => typeof x === "string" && !/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/.test(x);
const plainRun = (r: Run): string => typeof r === "string" ? r : "code" in r ? r.code : ("i" in r ? r.i : "b" in r ? r.b : r.a).map(plainRun).join("");
function boundedRecord(c: RawComment): DiscussionRecord {
  let body = capBody(c.body);
  // Link-heavy markup may be much larger than its visible text. Fall back to the same words.
  if (jsonBytes(body) > 16_000) body = capBody([body.flat().map(plainRun)]);
  return {
    id: cutText(c.id, 128), by: cutText(c.by, 256), body,
    ...(c.parent ? { parent: cutText(c.parent, 128) } : {}),
    ...(c.at !== undefined && Number.isFinite(c.at) ? { at: c.at } : {}),
    ...(c.score !== undefined && Number.isFinite(c.score) ? { score: c.score } : {}),
    ...(c.tag ? { tag: cutText(c.tag, 128) } : {}),
    ...(c.quote ? { quote: { ...(c.quote.by ? { by: cutText(c.quote.by, 256) } : {}), text: cutText(c.quote.text, 600) } } : {}),
    ...(c.link && c.link.length <= 2048 && safeHref(c.link) ? { link: c.link } : {}),
    ...(c.removed ? { removed: true } : {}), ...(c.hidden ? { hidden: true } : {}),
  };
}
function after(records: readonly { id: string }[], cursor: DiscussionCursor): number | null {
  if (!cursor.after) return 0;
  const at = records.findIndex(r => r.id === cursor.after);
  if (at < 0 || discussionHash(records.slice(0, at + 1).map(r => r.id).join("\n")) !== cursor.prefix) return null;
  return at + 1;
}
const continuation = (cursor: DiscussionCursor, list: readonly { id: string }[], end: number) => encodeDiscussionCursor({ ...cursor, after: list[end - 1]!.id, prefix: discussionHash(list.slice(0, end).map(r => r.id).join("\n")) });

export interface WindowSource {
  current?: string; next?: string; total?: number; partial?: boolean; stream?: boolean;
  /** A provider can continue a root's replies independently of the captured local reply prefix. */
  replies?: ReadonlyMap<string, { count: number; next: string }>;
}
/** A bounded capture. Display batches are a client concern; source pages and reply continuations are not. */
export function discussionWindow(parsed: ParsedDiscussion, link: string, token: string | undefined, now: number, source: WindowSource = {}): DiscussionResult {
  const cursor = decodeDiscussionCursor(token, link);
  if (!cursor) return unavailable("changed", link, now);
  const window: DiscussionWindow = { version: 2, state: !parsed.found ? "none" : parsed.closed && !parsed.comments.length ? "closed" : parsed.comments.length ? "ok" : "empty", records: [], link, fetchedAt: now, ...(source.partial !== undefined ? { partial: source.partial } : {}) };
  if (!parsed.found) return { kind: "window", window };
  const bounded = parsed.comments.map(boundedRecord);
  const roots = threadComments(bounded);
  if (!source.stream && !roots.length) window.state = parsed.closed ? "closed" : "empty";
  if (source.total !== undefined || !source.stream || (!source.next && !cursor.source)) window.total = source.total ?? countComments(roots);
  const base: DiscussionCursor = { key: discussionKey(link), scope: source.stream ? "stream" : "roots", ...(source.current ? { source: source.current } : {}) };
  const fits = () => window.records.length <= WINDOW_RECORDS && jsonBytes({ kind: "window", window }) <= WINDOW_BYTES - 1024;
  const push = (record: DiscussionRecord): boolean => { window.records.push(record); if (fits()) return true; window.records.pop(); return false; };
  const nextSource = source.next ? encodeDiscussionCursor({ key: base.key, scope: base.scope, source: source.next }) : undefined;
  if (source.stream) {
    if (cursor.scope === "replies") return unavailable("changed", link, now);
    const from = after(bounded, cursor);
    if (from === null) return unavailable("changed", link, now);
    let end = from;
    window.next = nextSource;
    while (end < bounded.length) {
      window.next = end + 1 < bounded.length ? continuation(base, bounded, end + 1) : nextSource;
      if (!push(bounded[end]!)) break;
      end++;
    }
    window.next = end < bounded.length && end > from ? continuation(base, bounded, end) : nextSource;
  } else if (cursor.scope === "replies") {
    const root = roots.find(r => r.id === cursor.root);
    if (!root) return unavailable("changed", link, now);
    const replies = root.replies ?? [];
    const from = after(replies, cursor);
    if (from === null) return unavailable("changed", link, now);
    let end = from;
    while (end < replies.length) {
      window.next = end + 1 < replies.length ? continuation(cursor, replies, end + 1) : source.replies?.get(root.id)?.next;
      const r = replies[end]!;
      if (!push({ ...boundedRecord(r), parent: root.id, ...(r.to ? { to: r.to } : {}) })) break;
      end++;
    }
    window.next = end < replies.length && end > from ? continuation(cursor, replies, end) : source.replies?.get(root.id)?.next;
  } else {
    const from = after(roots, cursor);
    if (from === null) return unavailable("changed", link, now);
    let end = from;
    while (end < roots.length) {
      const root = roots[end]!;
      const native = source.replies?.get(root.id);
      const replyCount = Math.max(root.replies?.length ?? 0, native?.count ?? 0);
      const replyNext = root.replies?.length ? encodeDiscussionCursor({ ...base, scope: "replies", root: root.id }) : native?.next;
      window.next = end + 1 < roots.length ? continuation(base, roots, end + 1) : nextSource;
      if (!push({ ...boundedRecord(root), ...(replyCount ? { replyCount, replyNext } : {}) })) break;
      end++;
    }
    window.next = end < roots.length && end > from ? continuation(base, roots, end) : nextSource;
    for (const record of [...window.records]) {
      const replies = roots.find(r => r.id === record.id)?.replies ?? [];
      for (let i = 0; i < replies.length; i++) {
        const r = replies[i]!;
        const prior = record.replyNext;
        record.replyNext = i + 1 < replies.length ? continuation({ ...base, scope: "replies", root: record.id }, replies, i + 1) : source.replies?.get(record.id)?.next;
        if (!push({ ...boundedRecord(r), parent: record.id, ...(r.to ? { to: r.to } : {}) })) { record.replyNext = prior; break; }
      }
    }
  }
  // Undefined properties are omitted on the wire and never persisted as Convex values.
  if (!window.next) delete window.next;
  for (const r of window.records) if (!r.replyNext) delete r.replyNext;
  if (!window.records.length && window.state === "ok" && (roots.length || bounded.length)) return unavailable("failed", link, now);
  if (!fits()) return unavailable("failed", link, now);
  return { kind: "window", window };
}

/** Validate stored/bridged JSON before it reaches either renderer. */
export function parseDiscussionResult(value: unknown): DiscussionResult | null {
  if (!object(value) || jsonBytes(value) > WINDOW_BYTES) return null;
  if (value.kind === "unavailable") return ["failed", "blocked", "changed", "disabled", "waiting", "restricted"].includes(String(value.reason)) && typeof value.link === "string" && typeof value.checkedAt === "number" && Number.isFinite(value.checkedAt) && (value.cached === undefined || parseDiscussionResult({ kind: "window", window: value.cached }) !== null) ? value as unknown as DiscussionResult : null;
  if (value.kind !== "window" || !object(value.window)) return null;
  const w = value.window;
  if (w.version !== 2 || !["ok", "empty", "closed", "none"].includes(String(w.state)) || typeof w.link !== "string" || typeof w.fetchedAt !== "number" || !Number.isFinite(w.fetchedAt) || !Array.isArray(w.records) || w.records.length > WINDOW_RECORDS) return null;
  if (w.total !== undefined && (typeof w.total !== "number" || !Number.isFinite(w.total) || w.total < 0)) return null;
  if (w.next !== undefined && (typeof w.next !== "string" || w.next.length > 4096)) return null;
  for (const field of ["partial", "phoneCopy"]) if (w[field] !== undefined && typeof w[field] !== "boolean") return null;
  let nodes = 0;
  const run = (r: unknown, depth: number): boolean => {
    if (++nodes > 100_000 || depth > 12) return false;
    if (typeof r === "string") return validText(r);
    if (!object(r)) return false;
    if (typeof r.code === "string") return validText(r.code);
    const children = r.i ?? r.b ?? r.a;
    return Array.isArray(children) && (r.a === undefined || validText(r.href)) && children.every(child => run(child, depth + 1));
  };
  for (const r of w.records) {
    if (!object(r) || typeof r.id !== "string" || !r.id || r.id.length > 128 || typeof r.by !== "string" || r.by.length > 256 || !Array.isArray(r.body) || !r.body.every(p => Array.isArray(p) && p.every(x => run(x, 0)))) return null;
    for (const field of ["parent", "to", "tag", "link", "replyNext"]) if (r[field] !== undefined && (typeof r[field] !== "string" || r[field].length > (field === "replyNext" ? 4096 : 2048))) return null;
    for (const field of ["at", "score", "replyCount"]) if (r[field] !== undefined && (typeof r[field] !== "number" || !Number.isFinite(r[field]))) return null;
    for (const field of ["hidden", "removed"]) if (r[field] !== undefined && typeof r[field] !== "boolean") return null;
    if (r.quote !== undefined && (!object(r.quote) || typeof r.quote.text !== "string" || r.quote.text.length > 600 || (r.quote.by !== undefined && (typeof r.quote.by !== "string" || r.quote.by.length > 256)))) return null;
  }
  return value as unknown as DiscussionResult;
}

/** Compatibility at the presentation boundary; source records remain flat in transport and persistence. */
export function windowThreads(records: Iterable<DiscussionRecord>): Comment[] {
  const list = [...records]; const byId = new Map(list.map(r => [r.id, r]));
  const roots = threadComments(list);
  for (const root of roots) for (const reply of root.replies ?? []) if (byId.get(reply.id)?.to) reply.to = byId.get(reply.id)!.to;
  for (const r of list) if (!r.parent && r.removed && r.replyCount && !roots.some(root => root.id === r.id)) roots.push({ id: r.id, by: "", body: [], removed: true, ...(r.hidden ? { hidden: true } : {}) });
  const order = new Map(list.map((r, i) => [r.id, i]));
  roots.sort((a, b) => order.get(a.id)! - order.get(b.id)!);
  return roots;
}
